import type { ReactNode } from "react";
import type { Company } from "@/types/companies-types";

/**
 * Canonical view-model for CompanyEntity. Callers adapt their raw
 * data (Company, ReportCompany, etc.) into this shape before passing
 * it to the primitive.
 */
export interface CompanyEntityData {
  id: string;
  name: string;
  logoUrl?: string;
  domain?: string;
  industry?: string;
  employeeCount?: number;
  location?: string;
  description?: string;
  website?: string;
  isExactMatch?: boolean;
  fitReason?: string;
  jobsCount?: number;
  contactsCount?: number;
  /**
   * Raw underlying record (Company) — preserved so callers that need
   * to pipe the original object into downstream actions (e.g.
   * GetContactsButton's companyContextData) can do so without a second
   * lookup. The primitive itself never reads fields off of this.
   */
  raw?: Record<string, unknown>;
}

export type CompanyEntityLayout = "card" | "row";

export type CompanyEntityDensity = "comfortable" | "compact";

export interface CompanyEntityActions {
  research?: boolean;
  save?: boolean;
  quickView?: boolean;
  expand?: boolean;
}

export interface CompanyEntityProps {
  company: CompanyEntityData;
  layout?: CompanyEntityLayout;
  density?: CompanyEntityDensity;
  actions?: CompanyEntityActions;
  /**
   * Primary action — "Research" / "Get Contacts". When omitted and the
   * `research` action is enabled, the primitive renders a
   * GetContactsButton under the hood. When provided, it takes over and
   * a plain EntityActionButton is rendered instead.
   */
  onResearch?: (company: CompanyEntityData) => void;
  onSave?: (company: CompanyEntityData) => void;
  onQuickView?: (company: CompanyEntityData) => void;
  /** Row-layout only. Controlled expand state. */
  expanded?: boolean;
  onToggleExpand?: (company: CompanyEntityData) => void;
  /** Card-layout only. Slot for embedded contact list / contact actions. */
  footer?: ReactNode;
  /** Whole-card click for card-layout; ignored for row-layout. */
  onCardClick?: (company: CompanyEntityData) => void;
  className?: string;
}

/**
 * Adapter from the wire-shape `Company` to the canonical
 * `CompanyEntityData`. Keeps callers from threading the raw shape
 * through the primitive.
 */
export function toCompanyEntityData(company: Company): CompanyEntityData {
  const location =
    company.locality || company.region || company.country_name || undefined;
  const website = company.domain || company.website || undefined;

  return {
    id: String(company.id),
    name: company.company_name,
    logoUrl: company.square_logo_url ?? undefined,
    domain: company.domain ?? undefined,
    industry: company.industry ?? undefined,
    employeeCount: company.employee_count ?? undefined,
    location,
    description: company.description ?? undefined,
    website,
    isExactMatch: company.isExactMatch,
    raw: company as unknown as Record<string, unknown>,
  };
}
